import type { EvidenceTagKind } from "./evidenceTags";

export interface JourneyStage {
  id: string;
  title: string;
  objective: string;
  interaction: string;
  tag: EvidenceTagKind;
  source: string;
  estimatedMinutes: number;
}

/**
 * Las 13 etapas del recorrido del aspirante en un CALE, reestructuradas contra
 * el texto íntegro del Anexo 60 de la Res. 20253040037125 de 2025 (lectura del
 * PDF completo, 2026-09-28: docs-3d/research/ANEXO60_PASO_A_PASO.md).
 * Secuencia maestra normativa: agendamiento → validación de identidad →
 * teórica → destreza individual → maniobras en vía pública → cierre.
 */
export const JOURNEY_STAGES: readonly JourneyStage[] = [
  {
    id: "agendamiento",
    title: "Agendamiento del turno",
    objective:
      "Comprender que el proceso inicia antes de llegar al CALE: el aspirante solicita el turno en la página web del centro, escoge la categoría de licencia y registra tipo y número de documento, nombres, fecha de nacimiento, sexo y escolaridad. El CALE asigna fecha, hora y lugar de cada prueba, que se agenda por separado.",
    interaction: "Revisar el formulario de agendamiento de demostración con datos completamente sintéticos.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.4.2 y 2.5.2.",
    estimatedMinutes: 3,
  },
  {
    id: "llegada",
    title: "Llegada y accesos",
    objective:
      "Ubicar el acceso peatonal independiente del vehicular, la bahía accesible señalizada (símbolo NTC 4139) y la zona de parqueo exclusiva para vehículos registrados en RUNT y de personas en condición de discapacidad.",
    interaction: "Recorrer el andén segregado y reconocer la señalización de ingreso.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 2.1.1 f) y 2.1 Nota 1; NTC 4139.",
    estimatedMinutes: 3,
  },
  {
    id: "recepcion-espera",
    title: "Recepción y sala de espera",
    objective:
      "Conocer la recepción y la sala de espera, espacios que el Anexo 60 exige diferenciados de la sala de examen, con área de almacenamiento de artículos personales no permitidos durante la evaluación (casilleros).",
    interaction: "Observar el flujo de personas entre recepción, espera y casilleros.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.1.1 b) y Nota 1 (NTC 4595); num. 2.1.1 c).",
    estimatedMinutes: 4,
  },
  {
    id: "validacion-identidad",
    title: "Validación de la identidad",
    objective:
      "Seguir el orden normativo exacto, que se repite antes de cada prueba: (a) documento de identidad original, (b) validación a través de RUNT y SICOV, (c) toma de fotografía en alta definición, (d) firma digitalizada. Si hay inconsistencia, el aspirante corrige la novedad y agenda de nuevo.",
    interaction: "Ubicarse en la estación de validación escénica: sin captura biométrica ni documentos reales.",
    tag: "SIMULADO",
    source: "Res. 37125/2025, Anexo 60 num. 1.4.3 y 2.5.3; requisitos previos num. 1.4.1 y 2.5.1 (RNPNJ, certificados CRC y CEA vigentes en RUNT/SICOV).",
    estimatedMinutes: 5,
  },
  {
    id: "privacidad",
    title: "Privacidad y datos sensibles",
    objective:
      "Informar que la huella y el reconocimiento facial son datos sensibles: su tratamiento exige autorización expresa y está prohibido por regla general salvo excepciones legales. Usarlos para fines distintos de los autorizados por la RNEC acarrea sanciones.",
    interaction: "Revisar el aviso de privacidad y el módulo educativo de protección de datos.",
    tag: "NORMATIVO",
    source: "Ley 1581/2012 arts. 5–6; Res. 8150/2026 (Supertransporte), Anexo Técnico.",
    estimatedMinutes: 3,
  },
  {
    id: "sala-teorica",
    title: "Sala de la prueba teórica",
    objective:
      "Conocer la sala: mínimo 2 m² por aspirante, iluminación NTC 6519-1, un cubículo por aspirante separado físicamente con equipo, cámara, mesa y silla. Durante la prueba solo pueden estar los aspirantes y el administrador de aula; el aspirante ingresa únicamente con su documento de identidad.",
    interaction: "Recorrer la sala y reconocer cubículos, cámaras y el puesto del administrador de aula.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.1.1 a) y 1.2 Nota 2; NTC 6519-1.",
    estimatedMinutes: 5,
  },
  {
    id: "prueba-teorica",
    title: "Desarrollo de la prueba teórica",
    objective:
      "Vivir la secuencia real: la plataforma despliega un resumen con los datos del aspirante y la categoría, que él verifica antes de iniciar; resuelve 40 preguntas (12 actitudinales + 28 de núcleos: movilidad 10, normas 6, señalización 6, vehículo 6) en máximo 70 minutos; al terminar valida de nuevo su identidad vía SICOV y recibe el resultado. Aprueba con mínimo 80 % en núcleos y 80 % en actitudinales, calificados de forma independiente.",
    interaction: "Sentarse en el cubículo, verificar el resumen de datos y responder la interacción de ejemplo (no reproduce preguntas oficiales).",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.2, 1.3 y 1.4.5.",
    estimatedMinutes: 8,
  },
  {
    id: "supervision-contingencia",
    title: "Supervisión y contingencias",
    objective:
      "Entender qué pasa cuando algo falla: si la falla es del CALE o de fuerza mayor se reinicia desde el agendamiento sin pago; si se soluciona en 10 minutos o menos, la prueba continúa reponiendo el tiempo; la inasistencia permite reagendar dentro de los 10 días calendario siguientes sin pago (una vez).",
    interaction: "Explorar los escenarios de suspensión y los ajustes razonables (ruta accesible, croquis de itinerario para hipoacusia).",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.4.4, 2.5.4 y 2.5.5.2 (croquis); Ley 1618/2013; NTC 6047.",
    estimatedMinutes: 4,
  },
  {
    id: "alistamiento",
    title: "Alistamiento del vehículo",
    objective:
      "Distinguir los tres momentos de la revisión: (1) diaria —labrado de llantas, aceite, refrigerante, kit de arrastre en motos—; (2) al inicio de cada prueba, en presencia del aspirante —luces y bocina, espejos, presión de llantas, combustible, frenos, cinturón—; (3) verificación por el centro de control de los dispositivos tecnológicos de vehículo y pista. El aspirante recibe esta información en la zona de entrega, junto al inicio de la primera maniobra, y hace el reconocimiento externo e interno del vehículo.",
    interaction: "Recorrer el vehículo blanco de examen (rótulo “PRUEBA” verde Pantone 3425, logo CALE y teléfono de la Supertransporte) y señalar los puntos de inspección.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 2.2.7, 2.3.1 Nota 8 y 2.5.5.1; distintivo num. 2.2.1.",
    estimatedMinutes: 6,
  },
  {
    id: "destreza-moto",
    title: "Destreza individual — motocicleta",
    objective:
      "Recorrer la pista A1/A2: el aspirante lleva la moto al cajón de inicio con el motor apagado y realiza, una única vez cada una y completamente solo en pista, las maniobras 0 a 7 (carreteo, franja angosta, eslalon baja y alta velocidad, aceleración y frenado, giro en U, obstáculo con frenado de emergencia, señales; A2 añade los ochos). Tiempos máximos: 4 min (A1) y 5 min (A2). Inicia con 100 puntos y aprueba con mínimo 60. La pista debe estar seca.",
    interaction: "Seguir el trazado señalizado, el cajón de inicio y las zonas de seguridad de 1 m con barreras.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 2.3.1 (Notas 3–8), 2.3.1.2.1–2.3.1.2.2, 2.4.1 y 2.5.5.1.",
    estimatedMinutes: 7,
  },
  {
    id: "destreza-auto",
    title: "Destreza individual — automóvil",
    objective:
      "Recorrer la pista B1/C1, físicamente separada de la de motos (prohibida la interacción entre aspirantes A1/A2 y B/C): maniobras 8 a 13 — avance y detención, reversa en curva para estacionamiento perpendicular, cambio de sentido en espacio limitado, estacionamiento en línea, semáforo y arranque en rampa — en máximo 15 minutos, con el mismo esquema de puntaje (100 → mínimo 60).",
    interaction: "Observar la secuencia de maniobras señalizadas y el vehículo con doble pedal y doble espejo.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 2.1 Nota 1, 2.2.5.2, 2.3.1.2.3 y 2.4.1.",
    estimatedMinutes: 7,
  },
  {
    id: "via-publica",
    title: "Maniobras en vía pública",
    objective:
      "Comprender la última prueba: solo en el mismo CALE, solo en horario diurno (termina a más tardar 18:00) y tras aprobar la destreza. La plataforma escoge aleatoriamente uno de mínimo tres recorridos de al menos 11 km (se recorren mínimo 10 km), con vías de 30 km/h y arteriales, giros a izquierda y derecha, PARE, semáforo y cruce peatonal. El instructor va al doble mando (si interviene sin emergencia, el aspirante es declarado no apto); en motos el evaluador dirige desde un auto de acompañamiento por intercomunicador.",
    interaction: "Observar la salida controlada a la vía representada y el vehículo de acompañamiento.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 2.3.2 (Notas 2, 4, 6 y 7) y 2.5.5.2.",
    estimatedMinutes: 7,
  },
  {
    id: "cierre",
    title: "Cierre y resultado",
    objective:
      "Cerrar el proceso: tras cada prueba el aspirante valida su identidad vía SICOV y el CALE le comunica el resultado (aprobado o no aprobado); si no aprueba recibe un reporte de fallas o de núcleos reprobados con el puntaje. Aprobada la vía pública, continúa el trámite de la licencia ante el RUNT. Esta simulación no genera certificados, QR ni resultados con validez.",
    interaction: "Regresar a recepción y revisar el resumen del recorrido y las fuentes.",
    tag: "NORMATIVO",
    source: "Res. 37125/2025, Anexo 60 num. 1.4.5, 2.5.5.1 y 2.5.5.2. Nota de estado: exigibilidad del examen CALE en revisión técnica (Circular Ext. 20261010000317 de 2026).",
    estimatedMinutes: 3,
  },
] as const;

export const TOTAL_ESTIMATED_MINUTES = JOURNEY_STAGES.reduce((acc, s) => acc + s.estimatedMinutes, 0);
